import ErrorHandlerService from '@/modules/common/services/error-handler.service'
import { GtrEvent } from '@/interfaces'
import Container from 'typedi'
import Notification from '@/modules/common/services/notification.service'
import { ValidationObserver, extend } from 'vee-validate'
import { Component, Prop, Ref, Vue } from 'vue-property-decorator'
import store from '@/bootstrap/store/store'
import { EventModuleType } from '@/modules/common/enums/event-module-types.enum'
import { mapState } from 'vuex'
import ScormService from '@/modules/level-two/services/scormcloud.service'
import { fileTypeCategories } from '@/bootstrap/global/file-types'
import GtrSuper from '@/modules/common/components/mixins/gtr-super.mixin'

extend('uniqueEventId', {
  validate: async (value: string) => {
    try {
      const response = await store.dispatch('event/eventIdExists', value.replace(/[^0-9a-z-]/gi, '_'))
      return response.data.exists ? 'That title has already been used.' : true
    } catch (error) {
      Container.get(ErrorHandlerService).error(error)
      return false
    }
  }
})

@Component({
  name: 'GtrEventCreationWizard',
  computed: {
    ...mapState('event', ['events'])
  }
})
export default class GtrCompanyEventsView extends GtrSuper {
  EventModuleType = EventModuleType;
  uploading = false;
  isIndefinite = false;
  mediaMimeTypes = fileTypeCategories.image.mimeTypes

  @Prop({ required: true, type: Object })
  company!: Record<string, any>;

  @Prop({ default: EventModuleType.EVENT })
  eventModuleType!: EventModuleType;

  @Prop({ required: true, type: Boolean })
  value!: boolean;

  @Ref()
  readonly step1!: InstanceType<typeof ValidationObserver>

  @Ref()
  readonly step2!: InstanceType<typeof ValidationObserver>

  @Ref()
  readonly step3!: InstanceType<typeof ValidationObserver>

  eventStep = 1;
  hideDetails = false;
  submitting = false;
  addressErrorMessage = false;

  newEvent: GtrEvent = {
    name: null,
    type: null,
    category: null,
    event_identifier: null,
    active_start_date: null,
    active_start_date_menu: false,
    active_end_date: null,
    active_end_date_menu: false,
    event_start_date: null,
    event_start_date_menu: false,
    event_end_date: null,
    event_end_date_menu: false,
    banner: null,
    favicon: null,
    browser_tab_title: null,
    address: null,
    timezone: null,
    latitude: null,
    longitude: null,
    address_data: null,
    company_uuid: null,
    module_type: this.eventModuleType,
    metadata: {
      course_description: null,
      prerequisites: [],
      estimated_time: null,
      instructor: null,
      scormFileName: null
    }
  }

  eventTypeItems: string[] = [
    'Appearance or Signing',
    'Attraction',
    'Camp, Trip, or Retreat',
    'Class, Training, or Workshop',
    'Concert or Performance',
    'Conference',
    'Convention',
    'Dinner or Gala',
    'Festival or Fair',
    'Game or Competition',
    'Meeting or Networking Event',
    'Other',
    'Party or Social Gathering',
    'Race or Endurance Event',
    'Rally',
    'Screening',
    'Seminar or Talk',
    'Tour',
    'Tournament',
    'Tradeshow, Consumer Show, or Expo'
  ];

  eventCategoryItems: string[] = [
    'Auto, Boat & Air',
    'Business & Professional',
    'Charity & Causes',
    'Community & Culture',
    'Family & Education',
    'Fashion & Beauty',
    'Film, Media & Entertainment',
    'Food & Drink',
    'Government & Politics',
    'Health & Wellness',
    'Hobbies & Special Interest',
    'Home & Lifestyle',
    'Music',
    'Other',
    'Performing & Visual Arts',
    'Religion & Spirituality',
    'School Activities',
    'Science & Technology',
    'Seasonal & Holiday',
    'Sports & Fitness',
    'Travel & Outdoor'
  ];

  get eventCreateModal () {
    return this.value
  }

  get isFamiliarUi (): boolean {
    return this.$hasPermission('ui.familiar_ui')
  }

  get titleCaseModuleType () {
    // Convert the event module type to a more readable format
    return this.eventModuleType.charAt(0).toUpperCase() + this.eventModuleType.slice(1).toLowerCase()
  }

  get lowerCaseModuleType (): string {
    return this.eventModuleType.toLowerCase()
  }

  get existingEventList () {
    const { events } = this.events.find(({ uuid }) => (uuid === this.company.uuid)) ?? { events: [] }
    return events.map(({ name: text, uuid: value }) => ({
      text,
      value
    }))
  }

  async submit () {
    try {
      this.submitting = true
      if (this.eventModuleType === EventModuleType.COURSE || this.eventModuleType === EventModuleType.SCORM_COURSE) {
        this.newEvent.module_type = this.eventModuleType // keep current module type
        this.newEvent.type = 'Course'
        this.newEvent.category = 'Education'
        this.newEvent.address = 'Online'
        this.newEvent.latitude = '0'
        this.newEvent.longitude = '0'
        this.newEvent.timezone = 'UTC'
        this.newEvent.active_start_date = (new Date()).toLocaleString()
        this.newEvent.active_end_date = this.newEvent.event_end_date
      } else {
        delete this.newEvent.metadata
        this.newEvent.event_start_date = this.convertFromEventTimezoneToUtc(this.$data.newEvent.event_start_date, this.newEvent.timezone)
        this.newEvent.event_end_date = this.convertFromEventTimezoneToUtc(this.$data.newEvent.event_end_date, this.newEvent.timezone)
        this.newEvent.active_start_date = this.convertFromEventTimezoneToUtc(this.$data.newEvent.active_start_date, this.newEvent.timezone)
        this.newEvent.active_end_date = this.convertFromEventTimezoneToUtc(this.$data.newEvent.active_end_date, this.newEvent.timezone)
      }
      if (this.newEvent.latitude && this.newEvent.longitude) {
        this.newEvent.latitude = this.newEvent.latitude.toString()
        this.newEvent.longitude = this.newEvent.longitude.toString()
      }
      let resetStep3 = false
      if (!(this.newEvent.banner instanceof File)) {
        this.newEvent.banner = null
        resetStep3 = true
      }
      if (!(this.newEvent.favicon instanceof File)) {
        this.newEvent.favicon = null
        resetStep3 = true
      }
      if (resetStep3) {
        const step3 = (this.$refs.step3 as any)
        step3.reset()
      }
      this.$store.dispatch('common/showLoader', { value: true })
      this.newEvent.company_uuid = this.$route.params.uuid
      this.eventStep = 4
      const eventData = await this.$store.dispatch('event/createEvent', this.$data.newEvent)
      const timer = ms => new Promise(resolve => setTimeout(resolve, ms))
      let isEventActive = eventData.status === 'ACTIVE'
      if (this.eventModuleType !== EventModuleType.SCORM_COURSE) {
        while (!isEventActive) {
          const result = await this.$store.dispatch('event/getEventDupe', { event_uuid: eventData.uuid })
          if (result.status === 'ACTIVE') {
            isEventActive = true
          } else if (result.status === 'FAILED_TO_CREATE') {
            Container.get(ErrorHandlerService).error(`${this.titleCaseModuleType} failed to create.`)
            return
          }
          await timer(3000)
        }
      }
      let scormJobStarted = false
      if (
        this.eventModuleType === EventModuleType.SCORM_COURSE &&
          this.newEvent.metadata?.scormFile instanceof File
      ) {
        const scormService = Container.get(ScormService)
        scormJobStarted = true

        const uploadResult = await scormService.uploadScorm(
          this.company.uuid,
          this.newEvent.metadata.scormFile,
          eventData.uuid
        )

        if (!uploadResult.success || !uploadResult.jobId) {
          Container.get(ErrorHandlerService).error('SCORM upload failed. Course will not launch.')
          return // STOP EXECUTION HERE
        }
        this.pollScormStatus(this.company.uuid, uploadResult.jobId, eventData.uuid)

        Container.get(Notification).success(
            `${this.titleCaseModuleType} record created. SCORM content is now processing in the background.`
        )
      }
      if (!scormJobStarted) {
        Container.get(Notification).success(`${this.titleCaseModuleType} successfully created.`)
      }
      this.finishEventModal()
      this.$emit('gtr-event-created')
    } catch (error) {
      const apiError = (error as any)?.response?.data
      let errorMessage: string

      if (apiError && apiError.error_detail) {
        errorMessage = apiError.error_detail
      } else if (apiError && apiError.message) {
        errorMessage = apiError.message
      } else if ((error as any).message) {
        errorMessage = (error as any).message
      } else {
        errorMessage = 'An unexpected error occurred. Please contact support.'
        Container.get(ErrorHandlerService).error(error)
      }
      if (errorMessage) {
        Container.get(Notification).error(`Upload Failed: ${errorMessage}`)
      }
    } finally {
      this.submitting = false
      this.$store.dispatch('common/hideLoader')
    }
  }

  onScormFileChange (file: File | File[]) {
    const selectedFile = Array.isArray(file) ? file[0] : file

    if (!selectedFile) return
    const maxFileSize = 1_000_000_000
    if (selectedFile.size > maxFileSize) {
      Container.get(Notification).error('SCORM file cannot exceed 1 GB.')
      if (!this.newEvent.metadata) {
        this.newEvent.metadata = {}
      }
      this.newEvent.metadata.scormFile = null
      return
    }

    if (!this.newEvent.metadata) {
      this.newEvent.metadata = {}
    }
    this.newEvent.metadata.scormFile = selectedFile
    this.newEvent.metadata.scormFileName = selectedFile.name
  }

  private pollScormStatus (companyUuid: string, jobId: string, eventUuid: string) {
    const pollInterval = 10000
    const scormService = Container.get(ScormService)
    const notification = Container.get(Notification)
    const errorHandler = Container.get(ErrorHandlerService)
    const storeDispatch = store.dispatch

    const checkStatus = async () => {
      try {
        const statusResponse = await scormService.getJobStatus(companyUuid, jobId)
        const status = statusResponse.status
        const moduleType = this.titleCaseModuleType || 'Course'

        if (status === 'COMPLETE') {
          notification.success(`${moduleType} content imported successfully! The course is now fully activated.`)
          await storeDispatch('event/updateEventStatus', {
            uuid: eventUuid,
            status: 'ACTIVE'
          })
          return
        }

        if (status === 'ERROR' || status === 'FAILED') {
          const msg =
            statusResponse.message ||
            statusResponse.importResult?.message ||
            'SCORM import failed due to invalid package or server error.'
          notification.error(`SCORM Import Failed: ${msg}`)
          errorHandler.error(`SCORM Import Job Failed (Job ID: ${jobId}): ${msg}`)
          await storeDispatch('event/updateEventStatus', {
            uuid: eventUuid,
            status: 'FAILED_TO_CREATE'
          })

          return // Stop polling
        }
        setTimeout(checkStatus, pollInterval)
      } catch (error) {
        errorHandler.error('Failed to check SCORM status. Retrying in 5 seconds...')
        setTimeout(checkStatus, 5000)
      }
    }
    setTimeout(checkStatus, pollInterval)
  }

  finishEventModal () {
    this.$emit('input', false)
    this.eventStep = 1
    this.step1.reset()
    this.step2.reset()
    this.step3.reset()
    this.resetNewEvent()
  }

  bannerFilesChange (file: File): void {
    this.newEvent.banner = file
  }

  faviconFilesChange (file: File): void {
    this.newEvent.favicon = file
  }

  prefillEventIdentifier (val: string): void {
    if (val) {
      this.newEvent.event_identifier = val.replace(/[^0-9a-z-]/gi, '_')
    }
  }

  handleBlur (): void {
    if (this.newEvent.address) {
      this.hideDetails = true
      this.addressErrorMessage = false
    } else {
      this.hideDetails = false
      this.addressErrorMessage = true
    }
  }

  getAddressData (address_data: Record<string, any>): void {
    if (!address_data) return
    this.newEvent = {
      ...this.newEvent,
      address_data,
      latitude: address_data.latitude,
      longitude: address_data.longitude
    }
    this.handleTimeZone()
  }

  handleTimeZone (): void {
    const { address_data, address, latitude, longitude } = this.newEvent
    if (address_data !== null) {
      this.hideDetails = true
      this.$store.dispatch('event/loadTimezone', { latitude, longitude })
        .then((data) => {
          if (data) {
            this.newEvent.timezone = data.data.timeZoneId
          }
        })
        .catch((err) => Container.get(ErrorHandlerService).error(err))
    } else {
      if (address) {
        this.hideDetails = false
      }
    }
  }

  private resetNewEvent () {
    this.newEvent = {
      name: null,
      type: null,
      category: null,
      event_identifier: null,
      active_start_date: null,
      active_start_date_menu: false,
      active_end_date: null,
      active_end_date_menu: false,
      event_start_date: null,
      event_start_date_menu: false,
      event_end_date: null,
      event_end_date_menu: false,
      banner: null,
      favicon: null,
      browser_tab_title: null,
      address: null,
      timezone: null,
      latitude: null,
      longitude: null,
      address_data: null,
      company_uuid: null,
      module_type: this.eventModuleType,
      metadata: {
        course_description: null,
        prerequisites: [],
        estimated_time: null,
        instructor: null,
        scormFileName: null
      }
    }
  }

  onIndefiniteToggle (indefinite: boolean): void {
    if (indefinite) {
      // Set event_start_date to the current date and time (local timezone)
      this.newEvent.event_start_date = new Date()
      // Set event_end_date to 12/31/2099 00:00 (local timezone)
      this.newEvent.event_end_date = new Date(2099, 11, 31, 0, 0, 0)
      if (this.newEvent.metadata) {
        this.newEvent.metadata.is_indefinite = true
      } else {
        this.newEvent.metadata = { is_indefinite: true }
      }
    } else {
      this.newEvent.event_start_date = null
      this.newEvent.event_end_date = null
    }
  }
}
